"use client"

import { Award, GraduationCap, User } from "lucide-react"
import { useUserProfile } from "@/hooks/stores/userStore"

interface UserProfileCardProps {
  // Optional props for customization
  level?: number
  levelName?: string
  userName?: string
  userGrade?: string
  rank?: number
  totalStudents?: number
  avatar?: string
}

export default function UserProfileCard({
  level,
  levelName,
  userName,
  userGrade,
  rank = 1,
  totalStudents = 100,
  avatar,
}: UserProfileCardProps) {
  // Get user data from Zustand store
  const storeUser = useUserProfile();
  
  // Use props if provided, otherwise use store data
  const userLevel = level || storeUser?.level || 1;
  const userLevelName = levelName || getLevelName(userLevel);
  const displayName = userName || storeUser?.name || "Student";
  const displayGrade = userGrade || storeUser?.grade || "Grade 7";
  const displayRank = rank;
  const displayTotalStudents = totalStudents;
  
  // Generate avatar if not provided
  const displayAvatar = avatar || storeUser?.avatarUrl || 
    `https://api.dicebear.com/7.x/avataaars/svg?seed=${displayName}`;

  // Function to get level name based on level
  function getLevelName(levelNum: number): string {
    const levelNames = [
      "Beginner",
      "Explorer", 
      "Learner",
      "Achiever",
      "Expert",
      "Master",
      "Champion",
      "Legend"
    ];
    
    if (levelNum <= levelNames.length) {
      return levelNames[levelNum - 1];
    }
    return `Level ${levelNum}`;
  }

  // Calculate percentile
  const percentile = Math.round((displayRank / displayTotalStudents) * 100);

  return (
    <section className="bg-gradient-to-r from-blue-50 via-white to-teal-50 p-6 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-6 mb-8 shadow-md border border-slate-200">
      {/* Left Section: Avatar and Info */}
      <div className="flex flex-col md:flex-row items-center gap-4">
        {/* Avatar with level badge */}
        <div className="relative">
          <img
            alt={`${displayName}'s avatar`}
            className="w-24 h-24 rounded-full border-4 border-white shadow-lg"
            src={displayAvatar}
          />
          {/* Level Badge */}
          <div className="absolute -bottom-2 -right-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-full w-10 h-10 flex items-center justify-center border-2 border-white shadow-md">
            <span className="font-bold text-sm">L{userLevel}</span>
          </div>
        </div>
        
        {/* User Info */}
        <div className="text-center md:text-left">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-5 h-5 text-blue-600" />
            <h2 className="text-2xl font-bold text-slate-900">
              {displayName}
            </h2>
          </div>
          
          <div className="flex items-center gap-2 mb-2">
            <GraduationCap className="w-4 h-4 text-teal-600" />
            <p className="text-slate-600 font-medium">
              {displayGrade} • {userLevelName}
            </p>
          </div>
          
          <p className="text-slate-500 text-sm">
            You're doing amazing! Keep up the great work!
          </p>
        </div>
      </div>
      
      {/* Right Section: Rank Badge */}
      <div className="text-center md:text-right">
        <div className="bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 py-3 px-6 rounded-full font-semibold flex flex-col items-center md:items-end gap-1 shadow-sm">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-600" />
            <span className="text-lg">Rank #{displayRank}</span>
          </div>
          <p className="text-sm text-amber-700">
            Top {percentile}% of {displayTotalStudents} students
          </p>
        </div>
        
        {/* Additional Stats */}
        {/* <div className="flex gap-4 mt-4 justify-center md:justify-end">
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-700">{storeUser?.totalPoints || 0}</p>
            <p className="text-xs text-slate-500">Points</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-teal-700">{storeUser?.streakDays || 0}</p>
            <p className="text-xs text-slate-500">Day Streak</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-amber-700">{storeUser?.tasksCompleted || 0}</p>
            <p className="text-xs text-slate-500">Tasks Done</p>
          </div>
        </div> */}
      </div>
    </section>
  )
}